import EmailIcon from "@mui/icons-material/Email";
import { getMembershipStatusChipColor } from "../../../utils/StatusColors";
import ViewDialog from "../../../components/ViewDialog";
interface DetailSection {
  title: string;
  items: DetailItem[];
}

interface DetailItem {
  label: string;
  value: string | number | React.ReactNode;
  isChip?: boolean;
  chipProps?: {
    color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    variant?: 'filled' | 'outlined';
    size?: 'small' | 'medium';
    icon?: React.ReactElement;
  };
}

interface ViewMembershipDialogProps {
  open: boolean;
  onClose: () => void;
  selectedMembership: any;
  onEditStatus?: (membership: any) => void;
}

export const ViewMembershipDialog: React.FC<ViewMembershipDialogProps> = ({
  open,
  onClose,
  selectedMembership,
  onEditStatus,
}) => {
  if (!selectedMembership) return null;

  const regularPrice = selectedMembership.membershipListing?.amount
    ? Number(selectedMembership.membershipListing.amount)
    : 0;

  const discountPercent =
    typeof selectedMembership.discountPercent === "number"
      ? selectedMembership.discountPercent
      : selectedMembership.discountPercent
        ? Number(selectedMembership.discountPercent)
        : null;

  const discountExpiry = selectedMembership.discountExpiryDate
    ? new Date(selectedMembership.discountExpiryDate)
    : null;

  const isDiscountActive =
    discountPercent && discountExpiry && !Number.isNaN(discountPercent)
      ? discountExpiry.getTime() >= Date.now()
      : false;

  const discountedPrice =
    discountPercent && !Number.isNaN(discountPercent)
      ? regularPrice * (1 - discountPercent / 100)
      : null;

  const sections: DetailSection[] = [
    {
      title: "Member Information",
      items: [
        {
          label: "Name",
          value: `${selectedMembership.user.firstName} ${selectedMembership.user.lastName}`,
        },
        {
          label: "Email",
          value: selectedMembership.user.email || "N/A",
          isChip: true,
          chipProps: {
            icon: <EmailIcon />,
            variant: "outlined",
            size: "small",
          },
        },
        {
          label: "Phone",
          value: selectedMembership.user.phoneNumber || "N/A",
          isChip: true,
          chipProps: {
            variant: "outlined",
            size: "small",
          },
        },
      ],
    },
    {
      title: "Membership Details",
      items: [
        {
          label: "Membership ID",
          value: selectedMembership.id.toUpperCase(),
        },
        {
          label: "Plan",
          value: selectedMembership.membershipListing.name,
        },
        {
          label: "Status",
          value: selectedMembership.status,
          isChip: true,
          chipProps: {
            color: getMembershipStatusChipColor(selectedMembership.status) as any,
            size: "small",
          },
        },
        {
          label: "Amount",
          value: `$${selectedMembership.amount}`,
        },
        {
          label: "Total Amount Paid",
          value: selectedMembership.totalPaid === null ? "-" : `$${selectedMembership.totalPaid}`,
        },
        {
          label: "Start Date",
          value: selectedMembership.startDate === null ? "-" : new Date(selectedMembership.startDate).toLocaleDateString() + " " + new Date(selectedMembership.startDate).toLocaleTimeString(),
        },
        {
          label: "Next Payment Date",
          value: selectedMembership.nextPaymentDate === null ? "-" : new Date(selectedMembership.nextPaymentDate).toLocaleDateString()  + " " + new Date(selectedMembership.nextPaymentDate).toLocaleTimeString(),
        },
        {
          label: "Membership Ends On",
          value: selectedMembership.endDate === null ? "-" : new Date(selectedMembership.endDate).toLocaleDateString()  + " " + new Date(selectedMembership.endDate).toLocaleTimeString(),
        },
        {
          label: "Cancelled At",
          value: selectedMembership.cancelledAt === null ? "-" : new Date(selectedMembership.cancelledAt || "").toLocaleDateString()  + " " + new Date(selectedMembership.cancelledAt || "").toLocaleTimeString(),
        },
      ],
    },
    {
      title: "Discount & Pricing",
      items: [
        {
          label: "Discount",
          value:
            discountPercent && !Number.isNaN(discountPercent) ? (
              discountExpiry ? (
                `${discountPercent}% off`
              ) : (
                `${discountPercent}% off (valid-until unknown)`
              )
            ) : (
              "No discount"
            ),
        },
        {
          label: "Valid Until",
          value: discountExpiry ? discountExpiry.toLocaleDateString() + " " + discountExpiry.toLocaleTimeString() : "-",
        },
        {
          label: "Price During Discount",
          value:
            isDiscountActive && discountedPrice !== null
              ? `$${discountedPrice.toFixed(2)}`
              : "-",
        },
        {
          label: "Price After Discount",
          value: regularPrice ? `$${regularPrice.toFixed(2)}` : "-",
        },
      ],
    },
  ];

  return (
    <ViewDialog
      open={open}
      onClose={onClose}
      title="Membership Details"
      sections={sections}
      primaryAction={onEditStatus ? {
        label: "Update Status",
        onClick: () => {
          onClose();
          onEditStatus(selectedMembership);
        },
      } : undefined}
    />
  );
};